/**
 * The one place every model call goes through.
 *
 * WHY: the scarce resource is the provider's concurrency, and nothing else in the system can see it. A job
 * is not a proxy for it — one extraction job fans out over every task, one grading job over every
 * submission — so bounding jobs bounds the wrong noun. Before this gate, 24 concurrent jobs could mean
 * hundreds of simultaneous requests, and the failure mode was 429s in the middle of a teacher's exam.
 *
 * WHAT IT IS NOT: a retry or fallback layer. Call sites keep their own validation loops; this decides WHEN
 * a call may start, and gives it an outer deadline so it can never hang forever.
 *
 * THE ROUTER UNDERNEATH RETRIES, AND THAT SETS THE SCALE OF THE DEADLINE. `@adornis/llm`'s `LLMCompletions.create`
 * runs its own loop — 5 attempts, sleeping 2s/4s/6s/8s between them — and the OpenRouter handler constructs
 * `new OpenAI({...})` without overriding that SDK's own defaults of `maxRetries: 2` and `timeout: 600_000`. So ONE
 * `create()` can legitimately occupy a permit for 5 x 3 x 10 minutes. `LLM_ABANDON_PERMIT_MS` is therefore not a
 * guard against an exotic silent hang; it is the bound a merely slow-and-erroring provider reaches through entirely
 * normal retries. Sequential retries, so provider CONCURRENCY is still bounded correctly — it is the per-permit
 * duration that is much larger than one request.
 *
 * TWO LANES, and this is the part that matters for how the product feels: `interactive` (a teacher waiting
 * on a chat reply) is served before `batch` (extraction, generation, grading), and batch can never occupy
 * the last quarter of the capacity. Without that reservation, one class being graded — hundreds of queued
 * calls — would put every chat message behind it. FIFO within a lane, so nothing starves.
 *
 * PER PROCESS, NOT PER FLEET. `LLM_CAPACITY` bounds this instance; N replicas present N times as much to
 * the provider. `LLM_EXPECTED_REPLICAS` divides the budget so the fleet total stays at what was intended —
 * see limits.ts.
 *
 * Usage: replace `new AdornisLLMRouter(opts)` with `getLLMRouter(opts)`; the `chat.completions.create` call
 * is unchanged. Streaming is handled — the permit is held until the stream is drained, not until the promise
 * resolves, because a stream's promise resolves before any tokens have arrived.
 */

import { logger } from '@adornis/base/logging.js';
// Two different classes ship under this name, and this gate needs one of each:
//  - RUNTIME: `llm-router.js`, whose `create` merges constructor options into the body
//    (`{...this.opts, ...body}`). Two call sites configure model/provider/temperature on the constructor and
//    depend on that merge; with empty options it behaves exactly like the other one, so it is safe for all.
//  - TYPE: the non-generic class from `index.js`, whose `create` accepts a full body (model, provider,
//    temperature, …). The generic class's declared overloads accept only messages/stream/response_format/
//    tools, so advertising it would reject the fourteen call sites that pass everything in the body.
import type { AdornisLLMRouter } from '@adornis/llm/server/index.js';
import { AdornisLLMRouter as RuntimeLLMRouter } from '@adornis/llm/server/llm-router.js';
import { currentJobSignal } from '../jobs/job-signal.js';
import { addJobAdmissionCheck } from '../jobs/load.js';
// Sizing lives with every other limit of the scheduling stack, so tuning is one file: see jobs/limits.ts.
import {
  LLM_ABANDON_PERMIT_MS as ABANDON_PERMIT_MS,
  LLM_CAPACITY as capacity,
  LLM_INTERACTIVE_RESERVE as interactiveReserve,
  LLM_STUCK_PERMIT_MS as STUCK_PERMIT_MS,
} from '../jobs/limits.js';

export type LlmLane = 'interactive' | 'batch';

/**
 * A call that outlived `LLM_ABANDON_PERMIT_MS`. Thrown INTO the caller rather than merely reclaiming the permit
 * behind its back: a caller left awaiting a dead socket is a job holding its lockKey for its entire runtime budget
 * having done nothing. The runner's own deadline would eventually abandon it, but with no reason attached — the log
 * would say only "too slow". Turning the hang into an ordinary rejection ten minutes earlier (see
 * `LLM_ABANDON_PERMIT_MS`, set below `MAX_JOB_RUNTIME_MS` for exactly this) is what lets the retry and backoff
 * machinery everywhere else do its job, with the real cause recorded.
 */
/**
 * The caller's own work was cancelled — its job hit its runtime deadline, or the process is draining.
 *
 * Distinct from `LlmCallAbandonedError` because the two mean opposite things to whoever catches them: abandoned is
 * "the provider is not answering, retrying may help"; cancelled is "nobody wants this result any more, do not retry".
 * Kept out of the `abandoned` counter for the same reason — that counter's whole value is that it should be zero.
 */
export class LlmCallCancelledError extends Error {
  constructor(public readonly lane: LlmLane) {
    super(`Model call cancelled before completing (lane: ${lane})`);
    this.name = 'LlmCallCancelledError';
  }
}

export class LlmCallAbandonedError extends Error {
  constructor(
    public readonly lane: LlmLane,
    public readonly heldMs: number,
  ) {
    super(`Model call abandoned after ${Math.round(heldMs / 1000)}s without settling (lane: ${lane})`);
    this.name = 'LlmCallAbandonedError';
  }
}

let activeInteractive = 0;
let activeBatch = 0;
/** Permits reclaimed from calls that never settled. A non-zero value means call sites are missing timeouts. */
let abandoned = 0;
/** Calls dropped because their caller was cancelled. Unlike `abandoned`, a healthy system may show these. */
let cancelled = 0;
const waiting: Record<LlmLane, Array<() => void>> = { interactive: [], batch: [] };

const active = (): number => activeInteractive + activeBatch;

/**
 * Deliberately swallows. Used as the placeholder rejecter before the expiry promise's executor has run, and as
 * the expiry's own keep-alive handler — see `acquire`.
 */
const ignoreError = (): void => undefined;

/**
 * Chat is COUNTED but effectively never blocked; batch yields to it.
 *
 * Deliberately not exempting chat from the gate altogether, which is the tempting version: bypassing a local
 * queue does not create provider capacity, it only moves the failure from a short wait here to a 429 there —
 * on the one call where that is least acceptable — and an ungated path would be invisible to `llmGateStats`.
 *
 * BOTH LANES TEST THE TOTAL against `capacity`, which is what makes it a real ceiling rather than an aspiration.
 * Testing interactive against a counter of its own (the earlier version) allowed `capacity + ceiling` calls in
 * flight whenever chat ramped up while batch was already running. Chat still never queues in practice, because
 * batch stops `interactiveReserve` short and those slots are therefore always free for it.
 *
 * The difference between the lanes is now exactly one term, which is the whole policy in one line.
 */
const canStart = (lane: LlmLane): boolean => active() + (lane === 'interactive' ? 0 : interactiveReserve) < capacity;

const takeSlot = (lane: LlmLane): void => {
  if (lane === 'interactive') activeInteractive += 1;
  else activeBatch += 1;
};

const freeSlot = (lane: LlmLane): void => {
  if (lane === 'interactive') activeInteractive -= 1;
  else activeBatch -= 1;
};

/**
 * Interactive first, then batch — and batch only while it would leave the reserve free.
 *
 * THE SLOT IS TAKEN HERE, BEFORE THE WAITER IS RESOLVED, and that is load-bearing. `resolve()` only schedules
 * a microtask; the waiter's own continuation does not run until this synchronous loop has finished. Letting
 * the waiter increment its own counter therefore left `canStart` reading unchanged numbers on every iteration,
 * so a single release drained the ENTIRE queue at once — the gate stopped bounding anything precisely when it
 * mattered. Transferring the slot keeps the counters truthful within the loop.
 */
function admitNext(): void {
  for (const lane of ['interactive', 'batch'] as const) {
    while (waiting[lane].length > 0 && canStart(lane)) {
      // Shift first, then take the slot: a slot taken for a waiter that turned out not to exist would be held
      // for the life of the process, and the whole point of this loop is to keep the counters honest.
      const next = waiting[lane].shift();
      if (!next) break;
      takeSlot(lane);
      next();
    }
  }
}

interface Permit {
  release: () => void;
  /**
   * Rejects with `LlmCallAbandonedError` once the permit has been held past `LLM_ABANDON_PERMIT_MS`; never
   * resolves. Raced against the underlying call (and against each chunk of a stream) so the deadline reaches
   * the caller instead of only freeing a counter.
   */
  readonly expiry: Promise<never>;
}

/**
 * Waits for a slot in `lane`, then returns a permit for it.
 *
 * The caller's lifetime comes from `currentJobSignal()` — the async-context scope `execute` opens around a handler.
 * Without it a queued waiter had no removal path at all: when a slot eventually freed it would take that slot and
 * send a request on behalf of a job that had already been abandoned, spending provider capacity and blocking a live
 * job behind it. The failure is not that the request never happens; it is that it happens anyway, late, for nobody.
 *
 * READ HERE, PER CALL, and not taken as a parameter on `getLLMRouter` — which was the first attempt and is wrong. A
 * router is often built once and reused (`default-grading.ts` memoises one at module scope), so a signal captured at
 * construction outlives the job that supplied it, and one job's cancellation would abort another's calls. Reading it
 * at acquisition binds it to the CALL, which is the thing actually being cancelled.
 *
 * It cannot be pushed any further down. `@adornis/llm`'s `create(body, options)` takes only `{wrapIntrospect}`, so
 * there is nowhere to hand a signal to the provider client — an abort stops us WAITING on a request, and never stops
 * the request itself. Which is exactly why the queued case is the one worth fixing: there, nothing has been sent yet.
 */
async function acquire(lane: LlmLane): Promise<Permit> {
  const signal = currentJobSignal();
  // Checked before anything is enqueued, so an already-dead caller never takes a slot.
  if (signal?.aborted) {
    cancelled += 1;
    throw new LlmCallCancelledError(lane);
  }

  // Either the slot is free and we take it synchronously, or `admitNext` takes it on our behalf before
  // resolving us. Both paths mean the same thing on the far side: the slot is already ours.
  if (canStart(lane)) takeSlot(lane);
  else
    await new Promise<void>((resolve, reject) => {
      const stopListening = (): void => signal?.removeEventListener('abort', onAbort);
      const waiter = (): void => {
        stopListening();
        resolve();
      };
      function onAbort(): void {
        const at = waiting[lane].indexOf(waiter);
        // `admitNext` already shifted us and took the slot on our behalf. The slot is ours, so honour the
        // acquisition rather than reject — the post-acquisition abort path below releases it properly.
        if (at === -1) {
          waiter();
          return;
        }
        waiting[lane].splice(at, 1);
        stopListening();
        cancelled += 1;
        reject(new LlmCallCancelledError(lane));
      }
      signal?.addEventListener('abort', onAbort, { once: true });
      waiting[lane].push(waiter);
    });

  const acquiredAt = Date.now();

  if (lane === 'interactive' && activeInteractive > interactiveReserve) {
    // Chat is using more than the space held for it, so batch is being squeezed. Normal under load, but it is
    // the signal that `LLM_MAX_CONCURRENT_CALLS` is smaller than this instance's real chat concurrency.
    logger.info(
      { activeInteractive, interactiveReserve, capacity },
      'Interactive model calls exceed their reserved share — batch work is yielding',
    );
  }

  let expire: (err: Error) => void = ignoreError;
  const expiry = new Promise<never>((_, reject) => {
    expire = reject;
  });
  // Nobody races the expiry on the happy path. `server/utils/server-async.ts` installs an `unhandledRejection`
  // hook, so an unobserved rejection is logged rather than fatal — but logged with no lane and no call site, as a
  // mystery entry every time a call is abandoned, and fatal again the day that hook is removed. A no-op handler
  // here does not stop `Promise.race` from seeing the rejection; it only stops the runtime calling it unhandled.
  expiry.catch(ignoreError);

  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    clearTimeout(stuckTimer);
    clearTimeout(abandonTimer);
    signal?.removeEventListener('abort', onCallerGone);
    freeSlot(lane);
    admitNext();
  };

  /**
   * The caller was cancelled while its request is already in flight. Same two actions as the abandon timer, and
   * deliberately so — reject the caller so its `finally` runs, and give the slot back.
   *
   * Freeing the slot while the request is still on the wire does briefly over-admit against the provider, since
   * nothing here can cancel an HTTP call the router gave us no signal for. That is the identical trade the abandon
   * timer already makes, and the alternative is worse: holding a slot for dead work until the 20-minute deadline,
   * when the whole reason we know it is dead is that its job already gave up.
   */
  function onCallerGone(): void {
    cancelled += 1;
    expire(new LlmCallCancelledError(lane));
    release();
  }
  signal?.addEventListener('abort', onCallerGone, { once: true });

  // A call this slow is worth knowing about while it is still only slow.
  const stuckTimer = setTimeout(() => {
    logger.error({ lane, active: active(), capacity }, 'A model-call permit has been held for over ten minutes');
  }, STUCK_PERMIT_MS);
  stuckTimer.unref();

  /**
   * The deadline. An HTTP client that hangs silently produces a promise which never settles either way, so no
   * `finally` ever runs: the permit is held for the life of the process AND the caller waits forever. Thirty of
   * those freeze the batch lane while chat carries on working, and the awaiting job holds its lockKey for its whole
   * runtime budget having produced nothing — blocking every other job that needs the same resource.
   *
   * Rejecting the caller (rather than only reclaiming the permit) is what makes this recoverable: the call site
   * sees an ordinary error, its `finally` runs, and the job fails and retries like any other.
   */
  const abandonTimer = setTimeout(() => {
    abandoned += 1;
    logger.error(
      { lane, active: active(), capacity, abandoned },
      'Model call exceeded its deadline without settling — rejecting the caller and reclaiming its permit',
    );
    expire(new LlmCallAbandonedError(lane, Date.now() - acquiredAt));
    release();
  }, ABANDON_PERMIT_MS);
  abandonTimer.unref();

  return { release, expiry };
}

const isAsyncIterable = (value: unknown): value is AsyncIterable<unknown> =>
  !!value && typeof (value as any)[Symbol.asyncIterator] === 'function';

/**
 * Holds the permit for the whole stream, and applies the same deadline to every chunk — a stream that stalls
 * halfway is the same failure as a request that never responds.
 *
 * `finally` covers an early `break` too: the consumer's `for await` calls `return()` on the generator, which
 * runs the finally block and both releases the permit and closes the upstream iterator.
 */
async function* gatedStream<T>(stream: AsyncIterable<T>, permit: Permit): AsyncGenerator<T> {
  const iterator = stream[Symbol.asyncIterator]();
  try {
    for (;;) {
      const next = await Promise.race([iterator.next(), permit.expiry]);
      if (next.done) return;
      yield next.value;
    }
  } finally {
    permit.release();
    try {
      await iterator.return?.();
    } catch (err) {
      logger.warn({ err }, 'Closing an abandoned model stream failed — the permit was released regardless');
    }
  }
}

export interface LlmGateStats {
  capacity: number;
  interactiveReserve: number;
  activeInteractive: number;
  activeBatch: number;
  queuedInteractive: number;
  queuedBatch: number;
  /** Permits force-released after never settling. Should be 0; anything else means a hanging provider call. */
  abandoned: number;
  /** Calls dropped because their caller was cancelled. Non-zero is normal — deploys and job deadlines cause it. */
  cancelled: number;
}

export const llmGateStats = (): LlmGateStats => ({
  capacity,
  interactiveReserve,
  activeInteractive,
  activeBatch,
  queuedInteractive: waiting.interactive.length,
  queuedBatch: waiting.batch.length,
  abandoned,
  cancelled,
});

/** Methods must stay bound to the real router — some carry private fields that a proxy `this` would break. */
const forward = (target: object, prop: PropertyKey): unknown => {
  const value = Reflect.get(target, prop);
  return typeof value === 'function' ? value.bind(target) : value;
};

/**
 * Wraps one router instance so every `create` waits for a permit first.
 *
 * A PROXY rather than a hand-built `{chat:{completions:{create}}}` literal: that shape silently dropped every
 * other property of the router, and the `as unknown as` cast at the call sites hid it. Nothing uses another
 * method today, but the next one to would have got `undefined is not a function` at runtime.
 */
function gated<T extends object>(router: T, lane: LlmLane): T {
  const create = async (body: any, createOptions?: any): Promise<any> => {
    const permit = await acquire(lane);
    try {
      const underlying = (router as any).chat.completions.create(body, createOptions);
      const result = await Promise.race([underlying, permit.expiry]);
      // A streaming response resolves immediately and does the work while it is iterated, so releasing here
      // would let the gate admit the next call before this one has sent a single token.
      if (isAsyncIterable(result)) return gatedStream(result, permit);
      permit.release();
      return result;
    } catch (err) {
      permit.release();
      throw err;
    }
  };

  // Resolved lazily on access so this never depends on `chat` existing at construction time.
  return new Proxy(router, {
    get(target, prop) {
      if (prop !== 'chat') return forward(target, prop);
      const chat = Reflect.get(target, 'chat') as object;
      return new Proxy(chat, {
        get(chatTarget, chatProp) {
          if (chatProp !== 'completions') return forward(chatTarget, chatProp);
          const completions = Reflect.get(chatTarget, 'completions') as object;
          return new Proxy(completions, {
            get: (completionsTarget, completionsProp) =>
              completionsProp === 'create' ? create : forward(completionsTarget, completionsProp),
          });
        },
      });
    },
  });
}

/**
 * Drop-in for `new AdornisLLMRouter({})` at a call site that passes the whole request in the body (model,
 * provider, temperature, messages). The overwhelming majority.
 */
export function getLLMRouter(options: Record<string, unknown> = {}, lane: LlmLane = 'batch'): AdornisLLMRouter {
  // No cast needed any more: `gated` is generic and returns the router's own type, which the generic runtime
  // class satisfies structurally. The old hand-built `{chat:{completions:{create}}}` literal needed an
  // `as unknown as` to pass — and that cast is what hid the dropped router surface it also returned.
  return gated(new RuntimeLLMRouter<any>(options as any), lane);
}

/**
 * Drop-in for `new AdornisLLMRouter({ provider, model, temperature })` at a call site that configures the
 * router once and then calls `create({ messages })` — the runtime class merges the two. A separate function
 * only because the two patterns need different declared types: this one's `create` accepts a partial body,
 * the other's requires a complete one.
 */
export function getConfiguredLLMRouter(
  options: Record<string, unknown>,
  lane: LlmLane = 'batch',
): RuntimeLLMRouter<any> {
  return gated(new RuntimeLLMRouter<any>(options as any), lane);
}

/**
 * Back-pressure into the job runner: while more batch calls are queued than the gate could run at once,
 * starting another job only deepens the queue and holds its memory meanwhile. Registered here rather than
 * in the job system so that neither side has to know about the other's internals — the runner asks "may I
 * admit?" and this answers, without ever mentioning a token or a provider.
 */
addJobAdmissionCheck(({ pool }) => {
  if (pool !== 'io') return null;
  const { queuedBatch } = llmGateStats();
  return queuedBatch >= capacity ? `llm-queue-deep:${queuedBatch}` : null;
});
